import { ChevronRight, Globe, MapPin, Plus, Swords, Tag } from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { CompetitorForm } from "./CompetitorForm";
import {
  filterCompetitors,
  latestUpdateByCompetitor,
  LINE_FILTERS,
  STATUS_FILTERS,
  type LineFilter,
  type StatusFilter,
} from "./competitors";
import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
  PillGroup,
  SectionLabel,
} from "./components";
import { formatShortDate } from "./dates";
import {
  isCompetitorLine,
  isTrackingStatus,
  lineLabelKey,
  trackingStatusLabelKey,
} from "./labels";
import { MiniMarkdown } from "./MiniMarkdown";
import { competitorPath } from "./routes";
import { TrackingStatusMenu } from "./TrackingStatus";
import type {
  CompetitorLine,
  MarketCompetitor,
  MarketCompetitorUpdate,
} from "./types";
import { hostOf, riseStyle } from "./util";

/** URL state: ?linea=…&estado=… (shareable, survives a reload). */
const useCompetitorFilters = () => {
  const [params, setParams] = useSearchParams();
  const lineParam = params.get("linea");
  const statusParam = params.get("estado");
  const line: LineFilter = isCompetitorLine(lineParam) ? lineParam : "all";
  const status: StatusFilter = isTrackingStatus(statusParam)
    ? statusParam
    : "all";
  const setFilters = (next: { line: LineFilter; status: StatusFilter }) => {
    const query: Record<string, string> = {};
    if (next.line !== "all") query.linea = next.line;
    if (next.status !== "all") query.estado = next.status;
    setParams(query, { replace: true });
  };
  return { line, status, setFilters };
};

export const CompetitorsView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const { line, status, setFilters } = useCompetitorFilters();

  const {
    data: competitors,
    isPending,
    error,
  } = useGetList<MarketCompetitor>("market_competitors", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: updates } = useGetList<MarketCompetitorUpdate>(
    "market_competitor_updates",
    {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "update_date", order: "DESC" },
    },
  );
  const latest = useMemo(
    () => latestUpdateByCompetitor(updates ?? []),
    [updates],
  );
  const visible = filterCompetitors(competitors ?? [], line, status);
  const filtered = line !== "all" || status !== "all";

  const addLabel = translate("market.competitors.add");
  const addButton = (
    <Button
      type="button"
      onClick={() => setCreating(true)}
      aria-label={isMobile ? addLabel : undefined}
      title={isMobile ? addLabel : undefined}
      className={cn(
        "btn-save shrink-0 rounded-full",
        isMobile ? "size-9 p-0" : "px-4",
      )}
    >
      <Plus aria-hidden className={isMobile ? "size-5" : "size-4"} />
      {isMobile ? null : addLabel}
    </Button>
  );

  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("market.competitors.heading")}</h2>
      <div className="space-y-2">
        <div className="mi-pills-row md:items-start md:justify-between">
          <PillGroup<LineFilter>
            label={translate("market.lines.label")}
            value={line}
            options={LINE_FILTERS.map((value) => ({
              value,
              label: translate(
                value === "all" ? "market.lines.all" : lineLabelKey(value),
              ),
            }))}
            onChange={(next) => setFilters({ line: next, status })}
          />
          {addButton}
        </div>
        <PillGroup<StatusFilter>
          label={translate("market.tracking_status.label")}
          value={status}
          options={STATUS_FILTERS.map((value) => ({
            value,
            label: translate(
              value === "all"
                ? "market.tracking_status.all"
                : trackingStatusLabelKey(value),
            ),
          }))}
          onChange={(next) => setFilters({ line, status: next })}
        />
      </div>
      {isPending ? (
        <LoadingCards />
      ) : error ? (
        <EmptyState icon={Swords} title={translate("market.error")} />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={Swords}
          title={translate(
            !competitors?.length
              ? "market.competitors.empty_all"
              : status !== "all"
                ? "market.competitors.empty_filtered"
                : "market.competitors.empty",
          )}
          hint={
            competitors?.length
              ? undefined
              : translate("market.competitors.empty_hint")
          }
          action={
            filtered && competitors?.length ? (
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => setFilters({ line: "all", status: "all" })}
              >
                {translate("market.competitors.clear_filters")}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
          {visible.map((competitor, index) => (
            <CompetitorCard
              key={competitor.id}
              competitor={competitor}
              latestNews={latest.get(String(competitor.id))}
              index={index}
            />
          ))}
        </div>
      )}
      <CompetitorForm
        open={creating}
        onOpenChange={setCreating}
        onCreated={(created) => navigate(competitorPath(created.id))}
      />
    </section>
  );
};

export const LineBadge = ({ line }: { line: CompetitorLine }) => {
  const translate = useTranslate();
  return (
    <span className="inline-flex h-6 max-w-full items-center rounded-full bg-primary/10 px-2.5 text-xs font-semibold text-primary dark:bg-primary/15">
      <span className="truncate">
        {isCompetitorLine(line) ? translate(lineLabelKey(line)) : line}
      </span>
    </span>
  );
};

const CompetitorCard = ({
  competitor,
  latestNews,
  index,
}: {
  competitor: MarketCompetitor;
  latestNews?: string;
  index: number;
}) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const titleId = `mi-competitor-${competitor.id}`;

  return (
    <article
      aria-labelledby={titleId}
      data-interactive
      className="mi-card mi-rise flex min-w-0 flex-col gap-3 p-4 md:p-5"
      style={riseStyle(index)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <h3
            id={titleId}
            className="text-[17px] leading-snug font-semibold wrap-anywhere"
          >
            <Link
              to={competitorPath(competitor.id)}
              className="outline-none hover:underline focus-visible:underline"
            >
              {competitor.name}
            </Link>
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            <LineBadge line={competitor.line} />
            <TrackingStatusMenu competitor={competitor} />
          </div>
        </div>
        {competitor.website ? (
          <ExternalAnchor
            href={competitor.website}
            className="max-w-[42%] text-sm"
          >
            <Globe aria-hidden className="mr-1 inline size-3.5" />
            {hostOf(competitor.website)}
          </ExternalAnchor>
        ) : null}
      </div>
      {competitor.location || competitor.entry_price ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {competitor.entry_price ? (
            <span className="inline-flex min-w-0 items-center gap-1 font-semibold">
              <Tag aria-hidden className="size-3.5 shrink-0 text-primary" />
              <span className="sr-only">
                {translate("market.competitors.entry_price")}:{" "}
              </span>
              <span className="truncate">{competitor.entry_price}</span>
            </span>
          ) : null}
          {competitor.location ? (
            <span className="inline-flex min-w-0 items-center gap-1 text-muted-foreground">
              <MapPin aria-hidden className="size-3.5 shrink-0" />
              <span className="sr-only">
                {translate("market.competitors.location")}:{" "}
              </span>
              <span className="truncate">{competitor.location}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      {competitor.value_proposition ? (
        <div>
          <SectionLabel>
            {translate("market.competitors.value_proposition")}
          </SectionLabel>
          <p className="line-clamp-3 text-[15px] wrap-anywhere">
            {competitor.value_proposition}
          </p>
        </div>
      ) : null}
      {competitor.description ? (
        <div>
          <SectionLabel>
            {translate("market.competitors.what_they_do")}
          </SectionLabel>
          <MiniMarkdown
            source={competitor.description}
            className="line-clamp-4"
          />
        </div>
      ) : null}
      {competitor.pricing && !competitor.entry_price ? (
        <div>
          <SectionLabel>{translate("market.competitors.pricing")}</SectionLabel>
          <p className="text-[15px] font-medium wrap-anywhere">
            {competitor.pricing}
          </p>
        </div>
      ) : null}
      <Link
        to={competitorPath(competitor.id)}
        className="-mx-1 mt-auto flex items-center justify-between gap-2 rounded-xl px-1 pt-2 text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        <span className="min-w-0 truncate">
          {latestNews
            ? translate("market.competitors.latest_news", {
                date: formatShortDate(latestNews, locale),
              })
            : translate("market.competitors.no_news")}
          {competitor.last_reviewed_at
            ? ` · ${translate("market.competitors.last_reviewed", {
                date: formatShortDate(competitor.last_reviewed_at, locale),
              })}`
            : ""}
        </span>
        <ChevronRight aria-hidden className="size-4 shrink-0" />
      </Link>
    </article>
  );
};
